import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const STEPS = [
    { id: 1, label: "Identité" },
    { id: 2, label: "Votre cabinet" },
    { id: 3, label: "Vos besoins" },
];

const SPECIALISATIONS = [
    "Traumatologie", "Rhumatologie", "Ostéopathie", "Uro Gynécologie",
    "Vestibulaire", "Thérapie Manuelle", "Rééducation Neuro-Musculaire",
    "Posturologie", "Réathlétisation", "Osocinétisme",
];
const EQUIPEMENTS = [
    "Ondes de choc", "Thécarthérapie", "Plateau de stabilométrie",
    "Ultrason", "Thérapie Laser", "Cryothérapie",
    "Appareil isocinétique", "Pressothérapie",
];
const PATHOLOGIES = [
    "Tendinopathies", "Syndrome Fémoro-patellaire", "Problématiques musculaires",
    "Déprogrammation Neuro-motrice", "Dos & Rachis", "Ligamentoplastie",
    "Arthrose", "Instabilité de cheville",
];
const OBJECTIFS = [
    "Développer vos connaissances",
    "Éviter les situations d'échec sur une pathologie précise",
    "Optimiser vos prises en charge",
    "Accéder à un label d'Expertise",
    "Diversification de votre activité",
    "Enrichir l'image de votre cabinet",
];

const initialForm = {
    nom: "", prenom: "", specialite: "", telephone: "", email: "", cabinet: "",
    nb_therapeutes: "", specialisations: [], specialisations_autres: "",
    equipements: [], equipements_autres: "", equipement_prefere: "",
    pathologies_difficiles: [], pathologies_autres: "",
    formations_passees: null, formations_passees_details: "",
    titre_expertise: null, titre_expertise_details: "",
    objectifs_formation: [],
};

function toggleInArr(arr, val) {
    return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
}

const inputCls =
    "w-full rounded-2xl border border-brand-ink/15 bg-white px-5 py-3 text-[15px] text-brand-ink placeholder:text-brand-ink/40 focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/20";
const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-ink/60";
const chipBase =
    "cursor-pointer select-none rounded-full border px-4 py-2 text-sm transition-all";

function Chip({ selected, onClick, children, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`${chipBase} ${
                selected
                    ? "border-brand-orange bg-brand-orange text-white"
                    : "border-brand-ink/15 bg-white text-brand-ink hover:border-brand-orange/50"
            }`}
        >
            {children}
        </button>
    );
}

export default function ParlerConseiller() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
    const setEvt = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const canNext = useMemo(() => {
        if (step === 1) {
            return ["nom", "prenom", "specialite", "telephone", "email", "cabinet"]
                .every((k) => (form[k] || "").trim().length > 0);
        }
        if (step === 2) return !!form.nb_therapeutes;
        return true;
    }, [step, form]);

    const submit = async () => {
        setSubmitting(true);
        setError("");
        try {
            await api.post("/conseiller-requests", form);
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            setError("Une erreur est survenue. Merci de réessayer ou de nous écrire à contact@cursus-physio.fr");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white" data-testid="page-parler-conseiller">
            <Helmet>
                <title>Parler à un conseiller — Cursus Physio</title>
                <meta name="description" content="Échangez avec un conseiller Cursus Physio. Un expert vous recontactera dans les 48h." />
            </Helmet>

            <section className="relative overflow-hidden bg-brand-ink py-24 text-white md:py-32">
                <div aria-hidden className="pointer-events-none absolute -top-32 right-0 h-[420px] w-[420px] rounded-full bg-brand-orange/15 blur-[140px]" />
                <div className="relative mx-auto max-w-4xl px-6 text-center">
                    <span className="inline-flex items-center rounded-full border border-white/25 bg-white/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/90 backdrop-blur-sm">
                        Parler à un conseiller
                    </span>
                    <h1 className="mt-8 font-display text-[36px] font-normal leading-[1.05] tracking-tightest sm:text-5xl lg:text-[64px]">
                        Un <em className="italic text-brand-orange">échange sur-mesure</em>
                        <br className="hidden lg:block" />{" "}
                        avec un expert Cursus Physio.
                    </h1>
                    <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
                        Répondez à quelques questions — un conseiller vous rappellera dans les 48h.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-3xl px-6 py-16 md:py-24">
                {done ? (
                    <div className="rounded-3xl border border-brand-orange/30 bg-[#F5F2EE] p-10 text-center" data-testid="conseiller-success">
                        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-orange text-white">
                            <Check className="h-7 w-7" strokeWidth={3} />
                        </div>
                        <h2 className="mt-6 font-display text-3xl text-brand-ink sm:text-4xl">
                            Merci, votre demande est enregistrée.
                        </h2>
                        <p className="mx-auto mt-4 max-w-xl text-base text-brand-ink/70">
                            <strong>Un conseiller vous recontactera dans les 48h.</strong>
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate("/")}
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-coal"
                        >
                            Retour à l&apos;accueil
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Progress */}
                        <div className="mb-10" data-testid="conseiller-progress">
                            <div className="flex items-center justify-between gap-2">
                                {STEPS.map((s) => (
                                    <div key={s.id} className="flex flex-1 items-center">
                                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                                            step >= s.id ? "bg-brand-orange text-white" : "bg-brand-ink/10 text-brand-ink/50"
                                        }`}>
                                            {s.id}
                                        </div>
                                        <div className="ml-3 hidden text-xs font-medium text-brand-ink/70 sm:block">
                                            {s.label}
                                        </div>
                                        {s.id !== STEPS.length && (
                                            <div className={`mx-3 h-px flex-1 ${step > s.id ? "bg-brand-orange" : "bg-brand-ink/10"}`} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={step}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                {step === 1 && (
                                    <div className="space-y-5" data-testid="step-1">
                                        <h2 className="font-display text-2xl text-brand-ink sm:text-3xl">Votre identité</h2>
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <div>
                                                <label className={labelCls}>Nom *</label>
                                                <input className={`mt-2 ${inputCls}`} value={form.nom} onChange={setEvt("nom")} data-testid="input-nom" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Prénom *</label>
                                                <input className={`mt-2 ${inputCls}`} value={form.prenom} onChange={setEvt("prenom")} data-testid="input-prenom" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Spécialité *</label>
                                                <input className={`mt-2 ${inputCls}`} value={form.specialite} onChange={setEvt("specialite")} data-testid="input-specialite" />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Tel / Mobile *</label>
                                                <input className={`mt-2 ${inputCls}`} value={form.telephone} onChange={setEvt("telephone")} data-testid="input-telephone" />
                                            </div>
                                            <div className="sm:col-span-2">
                                                <label className={labelCls}>Email *</label>
                                                <input type="email" className={`mt-2 ${inputCls}`} value={form.email} onChange={setEvt("email")} data-testid="input-email" />
                                            </div>
                                            <div className="sm:col-span-2">
                                                <label className={labelCls}>Nom du cabinet *</label>
                                                <input className={`mt-2 ${inputCls}`} value={form.cabinet} onChange={setEvt("cabinet")} data-testid="input-cabinet" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {step === 2 && (
                                    <div className="space-y-8" data-testid="step-2">
                                        <h2 className="font-display text-2xl text-brand-ink sm:text-3xl">Votre cabinet</h2>

                                        <div>
                                            <label className={labelCls}>1. Combien de thérapeutes exercent dans votre cabinet ? *</label>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {["1", "2", "3", "4+"].map((v) => (
                                                    <Chip key={v} selected={form.nb_therapeutes === v} onClick={() => set("nb_therapeutes")(v)} testid={`nb-${v}`}>{v}</Chip>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <label className={labelCls}>2. Vos spécialisations (plusieurs choix)</label>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {SPECIALISATIONS.map((s) => (
                                                    <Chip key={s} selected={form.specialisations.includes(s)} onClick={() => set("specialisations")(toggleInArr(form.specialisations, s))}>{s}</Chip>
                                                ))}
                                            </div>
                                            <input className={`mt-3 ${inputCls}`} placeholder="Autres spécialisations..." value={form.specialisations_autres} onChange={setEvt("specialisations_autres")} />
                                        </div>

                                        <div>
                                            <label className={labelCls}>3. Équipements présents dans votre cabinet (plusieurs choix)</label>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {EQUIPEMENTS.map((s) => (
                                                    <Chip key={s} selected={form.equipements.includes(s)} onClick={() => set("equipements")(toggleInArr(form.equipements, s))}>{s}</Chip>
                                                ))}
                                            </div>
                                            <input className={`mt-3 ${inputCls}`} placeholder="Autres équipements..." value={form.equipements_autres} onChange={setEvt("equipements_autres")} />
                                        </div>

                                        {(form.equipements.length > 0 || form.equipements_autres) && (
                                            <div>
                                                <label className={labelCls}>4. Parmi ces appareils, lequel vous apporte le plus de résultats ?</label>
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {[...form.equipements, ...(form.equipements_autres ? [form.equipements_autres] : [])].map((s) => (
                                                        <Chip key={s} selected={form.equipement_prefere === s} onClick={() => set("equipement_prefere")(s)}>{s}</Chip>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {step === 3 && (
                                    <div className="space-y-8" data-testid="step-3">
                                        <h2 className="font-display text-2xl text-brand-ink sm:text-3xl">Vos besoins de formation</h2>

                                        <div>
                                            <label className={labelCls}>5. Pathologies sur lesquelles vous rencontrez des difficultés (plusieurs choix)</label>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {PATHOLOGIES.map((s) => (
                                                    <Chip key={s} selected={form.pathologies_difficiles.includes(s)} onClick={() => set("pathologies_difficiles")(toggleInArr(form.pathologies_difficiles, s))}>{s}</Chip>
                                                ))}
                                            </div>
                                            <input className={`mt-3 ${inputCls}`} placeholder="Autres pathologies..." value={form.pathologies_autres} onChange={setEvt("pathologies_autres")} />
                                        </div>

                                        <div>
                                            <label className={labelCls}>6. Avez-vous participé à des programmes de formation ?</label>
                                            <div className="mt-3 flex gap-2">
                                                <Chip selected={form.formations_passees === true} onClick={() => set("formations_passees")(true)}>Oui</Chip>
                                                <Chip selected={form.formations_passees === false} onClick={() => set("formations_passees")(false)}>Non</Chip>
                                            </div>
                                            {form.formations_passees === true && (
                                                <textarea rows={3} className={`mt-3 ${inputCls}`} placeholder="Si oui, lesquels ?" value={form.formations_passees_details} onChange={setEvt("formations_passees_details")} />
                                            )}
                                        </div>

                                        <div>
                                            <label className={labelCls}>7. Avez-vous un titre d&apos;expertise (diplôme ou certification) ?</label>
                                            <div className="mt-3 flex gap-2">
                                                <Chip selected={form.titre_expertise === true} onClick={() => set("titre_expertise")(true)}>Oui</Chip>
                                                <Chip selected={form.titre_expertise === false} onClick={() => set("titre_expertise")(false)}>Non</Chip>
                                            </div>
                                            {form.titre_expertise === true && (
                                                <textarea rows={3} className={`mt-3 ${inputCls}`} placeholder="Si oui, lesquels ?" value={form.titre_expertise_details} onChange={setEvt("titre_expertise_details")} />
                                            )}
                                        </div>

                                        <div>
                                            <label className={labelCls}>8. Vos objectifs de formation (plusieurs choix)</label>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {OBJECTIFS.map((s) => (
                                                    <Chip key={s} selected={form.objectifs_formation.includes(s)} onClick={() => set("objectifs_formation")(toggleInArr(form.objectifs_formation, s))}>{s}</Chip>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>

                        {error && <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-3 text-sm text-red-700">{error}</div>}

                        <div className="mt-10 flex items-center justify-between gap-3">
                            <button
                                type="button"
                                disabled={step === 1}
                                onClick={() => setStep((s) => s - 1)}
                                className="inline-flex items-center gap-2 rounded-full border border-brand-ink/20 px-5 py-3 text-sm font-semibold text-brand-ink transition-colors hover:bg-brand-ink/5 disabled:opacity-40"
                                data-testid="btn-prev"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Précédent
                            </button>
                            {step < 3 ? (
                                <button
                                    type="button"
                                    disabled={!canNext}
                                    onClick={() => setStep((s) => s + 1)}
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition-all hover:shadow-xl disabled:opacity-40"
                                    data-testid="btn-next"
                                >
                                    Suivant
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    disabled={submitting}
                                    onClick={submit}
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand-orange/25 transition-all hover:shadow-xl disabled:opacity-60"
                                    data-testid="btn-submit"
                                >
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                    Envoyer ma demande
                                </button>
                            )}
                        </div>
                    </>
                )}

                <p className="mt-14 text-center text-xs text-brand-ink/50">
                    Vous préférez nous écrire directement ?{" "}
                    <Link to="/contact" className="text-brand-orange underline underline-offset-4">/contact</Link>
                </p>
            </section>
        </div>
    );
}
